import React, { useCallback, useEffect, useState, useRef } from 'react';
import { X, Send, Pin, Trash2, Plus, Maximize2, Minimize2, History, Sparkles, Bot, User, Loader2, MousePointer2, Zap, BarChart3, Wind, Globe, TrendingUp, AlertTriangle, ArrowLeftRight, Table2, Activity, SunMedium } from 'lucide-react';
import { cn } from '../ui/Button';
import { SelectedElement } from '../../hooks/useElementSelector';
import { useChatHistory, Message } from '../../hooks/useChatHistory';
import { RichMessageContent } from './InlineChatWidgets';
interface ViewContext {
  name: string;
  description: string;
}
interface AiChatPanelProps {
  isOpen: boolean;
  mode: 'docked' | 'floating';
  onClose: () => void;
  onModeChange: (mode: 'docked' | 'floating') => void;
  onDockedWidthChange?: (width: number) => void;
  onDockedResizingChange?: (resizing: boolean) => void;
  onStartSelection?: () => void;
  selectedElement?: SelectedElement | null;
  onClearSelection?: () => void;
  viewContext?: ViewContext;
}
// Parse multiple-choice options from assistant message
function parseChoices(content: string): {
  text: string;
  choices: {
    key: string;
    label: string;
  }[];
} | null {
  const choiceRegex = /\[([A-D])\]\s*(.+)/g;
  const choices: {
    key: string;
    label: string;
  }[] = [];
  let match;
  while ((match = choiceRegex.exec(content)) !== null) {
    choices.push({
      key: match[1],
      label: match[2].trim()
    });
  }
  if (choices.length >= 2) {
    // Remove the choice lines from the text to get the question part
    const textWithoutChoices = content.replace(/\[([A-D])\]\s*(.+)/g, '').trim();
    return {
      text: textWithoutChoices,
      choices
    };
  }
  return null;
}
const SUGGESTIONS = [{
  icon: Table2,
  label: 'Run delta analysis',
  prompt: 'Show me a run delta comparison table for the latest EPT-2, EC AIFS, and EC IFS model runs. Compare 06z vs 12z for wind 100m and temperature 2m across Central Europe. Flag any significant shifts.'
}, {
  icon: AlertTriangle,
  label: 'Model divergence',
  prompt: 'Build a model divergence matrix for the next 5 days. Compare EPT-2, EC AIFS, EC IFS, and GFS for wind generation in Germany. Flag any spreads >15% as trading signals.'
}, {
  icon: TrendingUp,
  label: 'Price spread opps',
  prompt: 'Analyze day-ahead vs intraday price spreads across DE, FR, NL, and NO2. Identify the top 3 arbitrage opportunities driven by weather forecast updates.'
}, {
  icon: Wind,
  label: 'Wind generation',
  prompt: 'Multi-model wind generation outlook for Germany and North Sea D+0 through D+5. Show capacity factors, ramp events, and compare EPT-2 vs EC IFS in a table.'
}, {
  icon: ArrowLeftRight,
  label: 'Cross-border flows',
  prompt: 'Analyze cross-border electricity flows and interconnector utilization for DE-FR, DE-NL, NO2-DE, and DE-AT. Show price differentials and flow direction opportunities.'
}, {
  icon: Activity,
  label: 'Balancing signals',
  prompt: 'What are the current balancing market signals for Central Europe? Show imbalance volumes, net positions, and flag upcoming ramp risks from renewable intermittency.'
}, {
  icon: SunMedium,
  label: 'Solar ramp risk',
  prompt: 'Assess solar ramp risk for tomorrow across Southern Germany and Italy. Compare cloud cover forecasts across models and estimate generation impact in GW.'
}, {
  icon: BarChart3,
  label: 'Demand forecast',
  prompt: 'Compare demand/load forecasts across available models for Germany D+0 to D+3. Show deltas vs actuals and highlight any temperature-driven demand shifts.'
}];
const DOCKED_MIN_W = 320;
const DOCKED_MAX_W = 640;
const FLOAT_MIN_W = 300;
const FLOAT_MIN_H = 280;
const FLOAT_DEFAULT_W = 380;
const FLOAT_DEFAULT_H_RATIO = 0.4; // 40% of viewport
export function AiChatPanel({
  isOpen,
  mode,
  onClose,
  onModeChange,
  onDockedWidthChange,
  onDockedResizingChange,
  onStartSelection,
  selectedElement,
  onClearSelection,
  viewContext
}: AiChatPanelProps) {
  const {
    chats,
    activeChatId,
    setActiveChatId,
    sendMessage,
    handleNewChat,
    togglePin,
    deleteChat,
    isStreaming
  } = useChatHistory();
  const [inputValue, setInputValue] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // --- Docked resize ---
  const [dockedWidth, setDockedWidth] = useState(380);
  const isResizingDocked = useRef(false);
  const [isDockResizing, setIsDockResizing] = useState(false);
  // --- Floating state ---
  const [floatPos, setFloatPos] = useState({
    x: 0,
    y: 0
  });
  const [floatSize, setFloatSize] = useState({
    w: FLOAT_DEFAULT_W,
    h: 0
  });
  const [floatInitialized, setFloatInitialized] = useState(false);
  const isDragging = useRef(false);
  const isResizingFloat = useRef<string | null>(null);
  const dragOffset = useRef({
    x: 0,
    y: 0
  });
  const resizeStart = useRef({
    x: 0,
    y: 0,
    w: 0,
    h: 0,
    px: 0,
    py: 0
  });
  const panelRef = useRef<HTMLDivElement>(null);
  const activeChat = chats.find(c => c.id === activeChatId);
  // Check if the last assistant message has choices
  const lastMessage = activeChat?.messages[activeChat.messages.length - 1];
  const lastMessageChoices = lastMessage?.role === 'assistant' && lastMessage.content ? parseChoices(lastMessage.content) : null;
  const handleSend = async (overrideContent?: string) => {
    const contentToSend = overrideContent || inputValue;
    if (!contentToSend.trim() || isStreaming) return;
    setInputValue('');
    await sendMessage(contentToSend, viewContext, selectedElement, onClearSelection);
  };
  // Keyboard shortcut for A/B/C/D selection
  useEffect(() => {
    if (!lastMessageChoices || isStreaming || !isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const key = e.key.toUpperCase();
      const choice = lastMessageChoices.choices.find(c => c.key === key);
      if (choice) {
        e.preventDefault();
        handleSend(`${choice.key}: ${choice.label}`);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lastMessageChoices, isStreaming, isOpen]);
  // --- Floating init ---
  useEffect(() => {
    if (mode === 'floating' && !floatInitialized) {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const h = vh < 700 ? vh - 104 : Math.round(vh * FLOAT_DEFAULT_H_RATIO);
      setFloatSize({
        w: FLOAT_DEFAULT_W,
        h
      });
      setFloatPos({
        x: vw - FLOAT_DEFAULT_W - 24,
        y: vh - h - 24
      });
      setFloatInitialized(true);
    }
  }, [mode, floatInitialized]);
  // --- Docked reset ---
  useEffect(() => {
    if (mode === 'docked') setFloatInitialized(false);
  }, [mode]);
  // --- Width notify ---
  useEffect(() => {
    if (mode === 'docked') onDockedWidthChange?.(dockedWidth);
  }, [dockedWidth, mode, onDockedWidthChange]);
  // --- Scroll to bottom ---
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [activeChat?.messages, isStreaming]);
  // --- Resize & Drag Handlers (same as before) ---
  const handleDockedResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingDocked.current = true;
    setIsDockResizing(true);
    onDockedResizingChange?.(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      if (!isResizingDocked.current) return;
      const newW = window.innerWidth - ev.clientX;
      setDockedWidth(Math.max(DOCKED_MIN_W, Math.min(DOCKED_MAX_W, newW)));
    };
    const onUp = () => {
      isResizingDocked.current = false;
      setIsDockResizing(false);
      onDockedResizingChange?.(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [onDockedResizingChange]);
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    isDragging.current = true;
    dragOffset.current = {
      x: e.clientX - floatPos.x,
      y: e.clientY - floatPos.y
    };
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      const nx = ev.clientX - dragOffset.current.x;
      const ny = ev.clientY - dragOffset.current.y;
      setFloatPos({
        x: Math.max(0, Math.min(window.innerWidth - 200, nx)),
        y: Math.max(0, Math.min(window.innerHeight - 100, ny))
      });
    };
    const onUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [floatPos]);
  const handleFloatResizeStart = useCallback((e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    isResizingFloat.current = handle;
    resizeStart.current = {
      x: floatPos.x,
      y: floatPos.y,
      w: floatSize.w,
      h: floatSize.h,
      px: e.clientX,
      py: e.clientY
    };
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      if (!isResizingFloat.current) return;
      const dx = ev.clientX - resizeStart.current.px;
      const dy = ev.clientY - resizeStart.current.py;
      const s = resizeStart.current;
      let newX = s.x,
        newY = s.y,
        newW = s.w,
        newH = s.h;
      if (handle.includes('e')) newW = Math.max(FLOAT_MIN_W, s.w + dx);
      if (handle.includes('w')) {
        newW = Math.max(FLOAT_MIN_W, s.w - dx);
        newX = s.x + (s.w - newW);
      }
      if (handle.includes('s')) newH = Math.max(FLOAT_MIN_H, s.h + dy);
      if (handle.includes('n')) {
        newH = Math.max(FLOAT_MIN_H, s.h - dy);
        newY = s.y + (s.h - newH);
      }
      setFloatSize({
        w: newW,
        h: newH
      });
      setFloatPos({
        x: newX,
        y: newY
      });
    };
    const onUp = () => {
      isResizingFloat.current = null;
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [floatPos, floatSize]);
  const handleNewChatClick = () => {
    handleNewChat();
    setShowHistory(false);
    onClearSelection?.();
  };
  const togglePinClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    togglePin(id);
  };
  const deleteChatClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    deleteChat(id);
  };
  const pinnedChats = chats.filter(c => c.pinned);
  const recentChats = chats.filter(c => !c.pinned);
  const isDocked = mode === 'docked';
  const panelStyle: React.CSSProperties = isDocked ? {
    width: dockedWidth
  } : {
    left: floatPos.x,
    top: Math.max(68, floatPos.y),
    width: floatSize.w,
    height: floatSize.h
  };
  // For floating mode, hide completely when not open
  if (!isOpen && !isDocked) return null;
  // --- Render a single message, with choice parsing ---
  const renderMessageContent = (msg: Message, isLastAssistant: boolean) => {
    if (!msg.content) {
      return <span className="flex items-center gap-1" data-id="element-41">
          <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce [animation-delay:-0.3s]" data-id="element-42" />
          <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce [animation-delay:-0.15s]" data-id="element-43" />
          <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" data-id="element-44" />
        </span>;
    }
    if (msg.role === 'assistant') {
      // Check for choices first
      if (isLastAssistant && !isStreaming) {
        const parsed = parseChoices(msg.content);
        if (parsed) {
          return <div className="space-y-3" data-id="element-45">
              <RichMessageContent content={parsed.text} hideCharts data-id="element-46" />
              <div className="space-y-1.5" data-id="element-47">
                {parsed.choices.map(choice => <button key={choice.key} onClick={() => handleSend(`${choice.key}: ${choice.label}`)} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] hover:bg-[#EC4899]/10 hover:border-[#EC4899]/30 transition-all text-left group" data-id="element-48">
                    <span className="w-6 h-6 rounded-md bg-white/[0.06] border border-white/[0.1] flex items-center justify-center text-[11px] font-bold text-white/50 group-hover:text-[#EC4899] group-hover:border-[#EC4899]/30 group-hover:bg-[#EC4899]/10 transition-colors flex-shrink-0" data-id="element-49">
                      {choice.key}
                    </span>
                    <span className="text-white/70 group-hover:text-white/90 transition-colors text-[13px]" data-id="element-50">
                      {choice.label}
                    </span>
                  </button>)}
              </div>
              <p className="text-[10px] text-white/25 text-center" data-id="element-51">
                Press{' '}
                <kbd className="px-1 py-0.5 rounded bg-white/[0.06] border border-white/[0.1] text-white/40 font-mono" data-id="element-52">
                  A
                </kbd>{' '}
                <kbd className="px-1 py-0.5 rounded bg-white/[0.06] border border-white/[0.1] text-white/40 font-mono" data-id="element-53">
                  B
                </kbd>{' '}
                <kbd className="px-1 py-0.5 rounded bg-white/[0.06] border border-white/[0.1] text-white/40 font-mono" data-id="element-54">
                  C
                </kbd>{' '}
                <kbd className="px-1 py-0.5 rounded bg-white/[0.06] border border-white/[0.1] text-white/40 font-mono" data-id="element-55">
                  D
                </kbd>{' '}
                to select
              </p>
            </div>;
        }
      }
      // Use RichMessageContent for all assistant messages (with charts hidden in panel)
      return <RichMessageContent content={msg.content} hideCharts data-id="element-56" />;
    }
    return <span className="whitespace-pre-wrap" data-id="element-57">{msg.content}</span>;
  };
  return <div ref={panelRef} className={cn('jua-ai-panel fixed z-50 flex flex-col', !isDockResizing && 'transition-transform duration-300 ease-in-out', isDocked ? 'right-0 bottom-0 top-16 md:top-0 bg-[#1E293B] border-l border-slate-600/50 shadow-xl' : 'bg-[#1E293B] rounded-2xl border border-slate-600/50 shadow-2xl shadow-black/30', isDocked && !isOpen && 'translate-x-full', isDocked && isOpen && 'translate-x-0')} style={panelStyle} data-id="element-58">
      {/* Docked resize handle */}
      {isDocked && <div onMouseDown={handleDockedResizeStart} className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-20 group hover:bg-[#EC4899]/10 transition-colors" data-id="element-59">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-slate-500 group-hover:bg-[#EC4899]/50 transition-colors" data-id="element-60" />
        </div>}

      {/* Floating resize handles */}
      {!isDocked && <>
          <div onMouseDown={e => handleFloatResizeStart(e, 'n')} className="absolute top-0 left-3 right-3 h-1.5 cursor-ns-resize z-20" data-id="element-61" />
          <div onMouseDown={e => handleFloatResizeStart(e, 's')} className="absolute bottom-0 left-3 right-3 h-1.5 cursor-ns-resize z-20" data-id="element-62" />
          <div onMouseDown={e => handleFloatResizeStart(e, 'w')} className="absolute left-0 top-3 bottom-3 w-1.5 cursor-ew-resize z-20" data-id="element-63" />
          <div onMouseDown={e => handleFloatResizeStart(e, 'e')} className="absolute right-0 top-3 bottom-3 w-1.5 cursor-ew-resize z-20" data-id="element-64" />
          <div onMouseDown={e => handleFloatResizeStart(e, 'nw')} className="absolute top-0 left-0 w-3 h-3 cursor-nwse-resize z-30" data-id="element-65" />
          <div onMouseDown={e => handleFloatResizeStart(e, 'ne')} className="absolute top-0 right-0 w-3 h-3 cursor-nesw-resize z-30" data-id="element-66" />
          <div onMouseDown={e => handleFloatResizeStart(e, 'sw')} className="absolute bottom-0 left-0 w-3 h-3 cursor-nesw-resize z-30" data-id="element-67" />
          <div onMouseDown={e => handleFloatResizeStart(e, 'se')} className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize z-30" data-id="element-68" />
        </>}

      {/* Header */}
      <div onMouseDown={!isDocked ? handleDragStart : undefined} className={cn('jua-ai-header flex items-center justify-between px-4 py-3.5 border-b border-slate-600/40 bg-[#253348] flex-shrink-0', !isDocked && 'cursor-grab active:cursor-grabbing rounded-t-2xl')} data-id="element-69">
        <div className="flex items-center gap-2.5" data-id="element-70">
          <div className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0" data-id="element-71">
            <img src="https://cdn.magicpatterns.com/uploads/oDxLcC3FkLNQvi2zVEpXDm/pasted-image.png" alt="Athena" className="w-full h-full object-cover" data-id="element-72" />
          </div>
          <div className="flex flex-col" data-id="element-73">
            <span className="font-semibold text-white/90 text-[15px] leading-tight" data-id="element-74">
              Athena AI
            </span>
            {viewContext && <span className="text-[10px] text-white/35 leading-tight" data-id="element-75">
                Viewing: {viewContext.name}
              </span>}
          </div>
        </div>
        <div className="flex items-center gap-0.5" data-id="element-76">
          <button onClick={handleNewChatClick} className="p-1.5 text-white/40 hover:text-white/80 hover:bg-white/[0.08] rounded-md transition-colors" title="New Chat" data-id="element-77">
            <Plus size={14} data-id="element-78" />
          </button>
          <button onClick={() => onModeChange(isDocked ? 'floating' : 'docked')} className="p-1.5 text-white/40 hover:text-white/80 hover:bg-white/[0.08] rounded-md transition-colors" title={isDocked ? 'Float' : 'Dock'} data-id="element-79">
            {isDocked ? <Minimize2 size={14} data-id="element-80" /> : <Maximize2 size={14} data-id="element-81" />}
          </button>
          <button onClick={() => setShowHistory(!showHistory)} className={cn('p-1.5 text-white/40 hover:text-white/80 hover:bg-white/[0.08] rounded-md transition-colors', showHistory && 'text-white/90 bg-white/[0.08]')} title="History" data-id="element-82">
            <History size={14} data-id="element-83" />
          </button>
          <button onClick={onClose} className="p-1.5 text-white/40 hover:text-white/80 hover:bg-white/[0.08] rounded-md transition-colors" title="Close" data-id="element-84">
            <X size={14} data-id="element-85" />
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden relative min-h-0" data-id="element-86">
        {/* History Sidebar Overlay */}
        <div className={cn('jua-ai-history absolute inset-0 z-10 bg-[#1E293B] transition-transform duration-300 ease-in-out flex flex-col', showHistory ? 'translate-x-0' : '-translate-x-full')} data-id="element-87">
          <div className="p-4 border-b border-slate-600/40" data-id="element-88">
            <button onClick={handleNewChatClick} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#EC4899]/15 text-[#EC4899] font-medium text-sm hover:bg-[#EC4899]/20 border border-[#EC4899]/25 transition-colors" data-id="element-89">
              <Plus size={16} data-id="element-90" /> New Chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-4" data-id="element-91">
            {pinnedChats.length > 0 && <div data-id="element-92">
                <div className="px-2 py-1 text-[11px] font-semibold text-white/35 uppercase tracking-wider flex items-center gap-1" data-id="element-93">
                  <Pin size={10} data-id="element-94" /> Pinned
                </div>
                <div className="space-y-0.5 mt-1" data-id="element-95">
                  {pinnedChats.map(chat => <div key={chat.id} onClick={() => {
                setActiveChatId(chat.id);
                setShowHistory(false);
              }} className={cn('group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors', activeChatId === chat.id ? 'bg-white/[0.1] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white')} data-id="element-96">
                      <span className="truncate text-sm" data-id="element-97">{chat.title}</span>
                      <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-98">
                        <button onClick={e => togglePinClick(e, chat.id)} className="p-1 hover:bg-white/[0.1] rounded text-[#EC4899]" data-id="element-99">
                          <Pin size={12} className="fill-current" data-id="element-100" />
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>}
            <div data-id="element-101">
              <div className="px-2 py-1 text-[11px] font-semibold text-white/35 uppercase tracking-wider" data-id="element-102">
                Recent
              </div>
              <div className="space-y-0.5 mt-1" data-id="element-103">
                {recentChats.map(chat => <div key={chat.id} onClick={() => {
                setActiveChatId(chat.id);
                setShowHistory(false);
              }} className={cn('group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors', activeChatId === chat.id ? 'bg-white/[0.1] text-white' : 'text-white/70 hover:bg-white/[0.06] hover:text-white')} data-id="element-104">
                    <span className="truncate text-sm" data-id="element-105">{chat.title}</span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-106">
                      <button onClick={e => togglePinClick(e, chat.id)} className="p-1 hover:bg-white/[0.1] rounded text-white/40 hover:text-white" data-id="element-107">
                        <Pin size={12} data-id="element-108" />
                      </button>
                      <button onClick={e => deleteChatClick(e, chat.id)} className="p-1 hover:bg-white/[0.1] rounded text-white/40 hover:text-red-400" data-id="element-109">
                        <Trash2 size={12} data-id="element-110" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="jua-ai-chat flex-1 flex flex-col min-w-0 min-h-0 bg-[#1A2332]" data-id="element-111">
          {activeChat ? <div className="flex-1 overflow-y-auto p-4 pt-10 space-y-4 min-h-0" data-id="element-112">
              {activeChat.messages.map((msg, idx) => {
            const isLastAssistant = msg.role === 'assistant' && idx === activeChat.messages.length - 1;
            return <div key={msg.id} className={cn('flex gap-3 max-w-[90%]', msg.role === 'user' ? 'ml-auto flex-row-reverse' : '')} data-id="element-113">
                    <div className={cn('w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-1', msg.role === 'assistant' ? '' : 'bg-slate-500 text-white')} data-id="element-114">
                      {msg.role === 'assistant' ? <img src="https://cdn.magicpatterns.com/uploads/oDxLcC3FkLNQvi2zVEpXDm/pasted-image.png" alt="Athena" className="w-7 h-7 rounded-full object-cover" data-id="element-115" /> : <User size={14} data-id="element-116" />}
                    </div>
                    {msg.role === 'user' ? <div className="p-3 rounded-2xl text-sm leading-relaxed bg-[#EC4899]/15 text-white/90 border border-[#EC4899]/20 rounded-tr-sm" data-id="element-117">
                        {renderMessageContent(msg, false)}
                      </div> : <div className="text-sm leading-relaxed text-white/80 pt-1 min-w-0 flex-1" data-id="element-118">
                        {renderMessageContent(msg, isLastAssistant)}
                      </div>}
                  </div>;
          })}
              <div ref={messagesEndRef} data-id="element-119" />
            </div> :
        // Welcome Screen
        <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center text-center" data-id="element-120">
              <div className="w-16 h-16 rounded-3xl overflow-hidden mb-6 shadow-[0_0_30px_rgba(236,72,153,0.15)]" data-id="element-121">
                <img src="https://cdn.magicpatterns.com/uploads/oDxLcC3FkLNQvi2zVEpXDm/pasted-image.png" alt="Athena" className="w-full h-full object-cover" data-id="element-122" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2" data-id="element-123">
                Athena Energy Intelligence
              </h2>
              <p className="text-sm text-slate-400 mb-2 max-w-xs" data-id="element-124">
                Run deltas, model divergences, price spreads, and trading
                signals — structured for speed.
              </p>
              <p className="text-[10px] text-slate-500 mb-6 max-w-xs" data-id="element-125">
                Powered by Jua.ai · Responses use tables and directional
                indicators for fast scanning
              </p>

              <div className="grid grid-cols-2 gap-2 w-full max-w-md" data-id="element-126">
                {SUGGESTIONS.map(s => <button key={s.label} onClick={() => handleSend(s.prompt)} className="jua-ai-suggestion flex flex-col items-start gap-2 p-3 rounded-xl bg-[#253348]/50 hover:bg-[#253348] border border-slate-700/50 hover:border-slate-600 transition-all text-left group" data-id="element-127">
                    <s.icon size={16} className="text-[#EC4899] opacity-70 group-hover:opacity-100" data-id="element-128" />
                    <span className="text-xs font-medium text-slate-300 group-hover:text-white" data-id="element-129">
                      {s.label}
                    </span>
                  </button>)}
              </div>
            </div>}

          {/* Input Area - Always Visible */}
          <div className="jua-ai-input p-3 border-t border-slate-600/40 bg-[#1E293B] flex-shrink-0 space-y-2 rounded-b-2xl" data-id="element-130">
            {selectedElement && <div className="flex items-center justify-between bg-[#EC4899]/10 border border-[#EC4899]/20 rounded-lg px-3 py-1.5" data-id="element-131">
                <div className="flex items-center gap-2 overflow-hidden" data-id="element-132">
                  <MousePointer2 size={12} className="text-[#EC4899] flex-shrink-0" data-id="element-133" />
                  <span className="text-xs text-[#EC4899] font-medium truncate" data-id="element-134">
                    Analyzing:{' '}
                    <span className="text-white/90" data-id="element-135">
                      {selectedElement.description}
                    </span>
                  </span>
                </div>
                <button onClick={onClearSelection} className="text-[#EC4899]/60 hover:text-[#EC4899] p-0.5 rounded-full hover:bg-[#EC4899]/10 transition-colors" data-id="element-136">
                  <X size={12} data-id="element-137" />
                </button>
              </div>}

            <div className="relative flex gap-2" data-id="element-138">
              <button onClick={onStartSelection} className="jua-ai-selector-btn p-2.5 rounded-xl bg-[#253348] border border-slate-600/40 text-slate-400 hover:text-white hover:border-slate-500 transition-colors flex-shrink-0" title="Select element to analyze" data-id="element-139">
                <MousePointer2 size={18} data-id="element-140" />
              </button>

              <div className="relative flex-1" data-id="element-141">
                <input value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && !isStreaming && handleSend()} placeholder={isStreaming ? 'Athena is thinking...' : 'Ask Athena anything...'} className="jua-ai-input-field w-full h-10 px-4 pr-10 rounded-xl bg-[#253348] border border-slate-600/40 text-sm text-white/90 placeholder:text-white/30 focus:outline-none focus:border-[#EC4899]/40 focus:ring-2 focus:ring-[#EC4899]/10 transition-all" data-id="element-142" />
                <button onClick={() => handleSend()} disabled={!inputValue.trim() || isStreaming} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#EC4899] hover:bg-[#EC4899]/15 rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-colors" data-id="element-143">
                  {isStreaming ? <Loader2 size={16} className="animate-spin" data-id="element-144" /> : <Send size={16} data-id="element-145" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}